import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { DomSanitizer } from '@angular/platform-browser';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { PromptTypes } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import {
   getSelectedAccountId,
   getSelectedAccountShortName,
} from 'src/app/store/select-account/select-account.selector';
import { getExtension } from '../../extension-details/state/extension-detail.selectors';
import { AddPromptData } from '../models/add-prompt-data';
import { RecordedAudioOutput } from '../models/recorded-prompt';
import { AddPromptApiService } from '../services/add-prompt-api.service';
import { AudioRecordingService } from '../services/audio-recording.service';
import {
   clearMicrophoneRecording,
   createPromptAndAddAnotherSuccess,
   recordPromptFromMicrophone,
} from '../state/add-prompt.actions';
import { getRecordedFileState } from '../state/add-prompt.selectors';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-add-prompt-view',
   styleUrls: ['./add-prompt-view.component.scss'],
   templateUrl: './add-prompt-view.component.html',
   standalone: false,
})
export class AddPromptViewComponent implements OnInit, OnDestroy {
   @ViewChild('name') name: ElementRef;

   @Input() includeAddAnother: boolean;

   @Input() callState: CallState;

   @Output() formSubmitted = new EventEmitter<AddPromptData>();

   @Output() callInitiated = new EventEmitter<boolean>();

   @Output() formCanceled = new EventEmitter<void>();

   @Output() recordingPromptCallCancelled = new EventEmitter<void>();

   @Output() saveAndAddAnother = new EventEmitter<AddPromptData>();

   @Input() form: UntypedFormGroup;

   @Input() creatingNewFromExtension: boolean;

   @Input() includeReusableOption: boolean;

   @Input() accountId: number | null;

   @Input() accountShortName: string | null;

   @Input() onlyPromptTypeOption: PromptTypes;

   @Input() excludedPromptTypes: PromptTypes[] = [];

   @Input() includedPromptTypes: PromptTypes[] | null;

   @Input() routeToNewPromptAfterSave: boolean | null;

   @Input() recordingPromptCallStatus: string | null;

   //if promptIdIfUpdatingExistingPromptFile has a value we are updating an existing prompt file. Prompt related controls will be hidden and on save only a file will be created. User will need to save the prompt to update the prompt fileId otherwise the new file will be deleted on cancel.

   @Input() promptIdIfUpdatingExistingPromptFile: number | null;

   get dontIncludeRecordingOption(): boolean {
      return this.onlyPromptTypeOption === PromptTypes.MusicOnHold;
   }

   blobObs: Observable<RecordedAudioOutput | null>;

   promptTypes = Object.values(PromptTypes);

   createPromptOptionValue = 'file';

   isRecording = false;

   recording = '';

   blobUrl;

   fileData;

   audioSrc: string | null = null;

   addPromptApiService: AddPromptApiService;

   routeToExtensionSelect: string;

   constructor(
      private store: Store,
      private audioRecordingService: AudioRecordingService,
      private sanitizer: DomSanitizer,
      private changeDetectorRef: ChangeDetectorRef,
      private actions$: Actions
   ) {
      this.audioRecordingService.recordingFailed().subscribe(() => {
         return (this.isRecording = false);
      });

      this.store.pipe(select(getRecordedFileState)).subscribe((data) => {
         if (data?.blob) {
            this.fileData = data;
            const objectUrl = URL.createObjectURL(data.blob);
            this.blobUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
            this.audioSrc = objectUrl; // raw string for the audio element
            this.saveRecording();
            this.changeDetectorRef.markForCheck();
         }
      });

      this.actions$.pipe(ofType(createPromptAndAddAnotherSuccess)).subscribe(() => {
         this.clearPromptFields();
         this.clearRecordedData();
         this.form.markAsPristine();
         this.form.markAsUntouched();
         this.name.nativeElement.focus();
      });
   }

   ngOnInit(): void {
      this.store.select(getSelectedAccountId).subscribe((accountId) => {
         this.accountId = accountId;
      });
      this.store.select(getSelectedAccountShortName).subscribe((accountShortName) => {
         this.accountShortName = accountShortName;
      });
      this.store.select;
      this.form.addControl('destinationExtension', new UntypedFormControl('', Validators.nullValidator));

      this.blobObs = this.audioRecordingService.getRecordedBlob();

      if (this.dontIncludeRecordingOption) {
         this.createPromptOptionValue = 'file';
      }
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onCreate(type: string): void {
      if (this.form.valid) {
         switch (type) {
            case 'file': {
               this.formSubmitted.emit();
               break;
            }
            case 'microphone': {
               if (this.fileData) {
                  this.formSubmitted.emit();
               }
               break;
            }
            case 'recording-extension': {
               this.callInitiated.emit(false);
               break;
            }
            case 'recording-external': {
               this.callInitiated.emit(false);
               break;
            }
         }
      }
   }

   onCreateAndAddAnother(type: string) {
      if (this.form.valid) {
         switch (type) {
            case 'file': {
               this.saveAndAddAnother.emit();
               break;
            }
            case 'microphone': {
               if (this.fileData) {
                  this.saveAndAddAnother.emit();
               }
               break;
            }
            case 'recording-extension': {
               this.callInitiated.emit(true);
               break;
            }
            case 'recording-external': {
               this.callInitiated.emit(true);
               break;
            }
         }
      }
   }

   promptDisplayWith(option: PromptTypes): string {
      return `${option ?? ''}`;
   }

   onCreateOptionChange(event: MatButtonToggleChange) {
      if (this.createPromptOptionValue === 'microphone' && event.value !== 'microphone') {
         this.abortRecording();
      }

      this.createPromptOptionValue = event.value;
      this.form.get('destinationExtension')?.setValue(null);
      this.form.get('destinationNumber')?.setValue(null);
      this.form.get('fileAttachment')?.setValue(null);
      if (event.value === 'file') {
         this.form.get('fileAttachment')?.setValidators(Validators.required);
         this.form.get('fileAttachment')?.updateValueAndValidity();
         this.form.get('destinationExtension')?.setValidators(null);
         this.form.get('destinationExtension')?.updateValueAndValidity();
         this.form.get('destinationNumber')?.setValidators(null);
         this.form.get('destinationNumber')?.updateValueAndValidity();
      } else if (event.value === 'microphone') {
         this.form.get('fileAttachment')?.setValidators(null);
         this.form.get('fileAttachment')?.updateValueAndValidity();
         this.form.get('destinationExtension')?.setValidators(null);
         this.form.get('destinationExtension')?.updateValueAndValidity();
         this.form.get('destinationNumber')?.setValidators(null);
         this.form.get('destinationNumber')?.updateValueAndValidity();
      } else if (event.value === 'recording-extension') {
         this.form.get('fileAttachment')?.setValidators(null);
         this.form.get('fileAttachment')?.updateValueAndValidity();
         this.form.get('destinationExtension')?.setValidators(Validators.required);
         this.form.get('destinationExtension')?.updateValueAndValidity();
         this.form.get('destinationNumber')?.setValidators(null);
         this.form.get('destinationNumber')?.updateValueAndValidity();
      } else {
         this.form.get('fileAttachment')?.setValidators(null);
         this.form.get('fileAttachment')?.updateValueAndValidity();
         this.form.get('destinationExtension')?.setValidators(null);
         this.form.get('destinationExtension')?.updateValueAndValidity();
         this.form.get('destinationNumber')?.setValidators([Validators.required, phoneNumberValidator()]);
         this.form.get('destinationNumber')?.updateValueAndValidity();
      }
   }

   // onRecordOptionChange(event: MatRadioChange) {
   //    this.abortRecording();
   //    this.recordPromptOptionValue = event.value;
   // }

   // startRecording() {
   //    if (!this.isRecording) {
   //       this.isRecording = true;
   //       this.audioRecordingService.startRecording();
   //       this.recording = 'Recording';
   //    }
   // }

   // abortRecording() {
   //    if (this.isRecording) {
   //       this.isRecording = false;
   //       this.audioRecordingService.abortRecording();
   //       this.recording = '';
   //    }
   //    this.clearRecordedData();
   // }

   // stopRecording() {
   //    if (this.isRecording) {
   //       this.audioRecordingService.stopRecording();
   //       this.isRecording = false;
   //       this.recording = '';
   //    }
   //    this.store.dispatch(recordPromptFromMicrophone());
   // }

   // clearRecordedData() {
   //    this.blobUrl = null;
   //    this.audioRecordingService.clearRecordedBlob();
   //    this.store.dispatch(clearMicrophoneRecording());
   //    this.form.patchValue({
   //       file: null,
   //       fileAttachment: null,
   //    });
   // }

   // ngOnDestroy(): void {
   //    this.abortRecording();
   // }

   // saveRecording() {
   //    const file = new File([this.fileData.blob], this.fileData.title);
   //    this.form.patchValue({
   //       file: {
   //          name: this.fileData.title,
   //          // eslint-disable-next-line sort-keys
   //          metaTag: this.fileData.title,
   //       },
   //       fileAttachment: file,
   //    });
   // }

   // validateRecording(): boolean {
   //    return this.fileData ? false : true;
   // }

   startRecording() {
      if (!this.isRecording) {
         this.isRecording = true;
         this.audioRecordingService.startRecording();
         this.recording = 'Recording';
      }
   }

   abortRecording() {
      if (this.isRecording) {
         this.isRecording = false;
         this.audioRecordingService.abortRecording();
         this.recording = '';
      }
      this.clearRecordedData();
   }

   stopRecording() {
      if (this.isRecording) {
         let wavName;
         if (this.creatingNewFromExtension) {
            this.store
               .pipe(select(getExtension))
               .pipe(first())
               .subscribe((extension) => {
                  wavName = `${this.accountShortName}-${extension?.number}-${this.formatDate()}-prompt.wav`;
               });
         } else {
            wavName = `${this.accountShortName}-${this.formatDate()}-prompt.wav`;
         }
         this.audioRecordingService.stopRecording(wavName);
         this.recording = 'Stopped';
         this.isRecording = false;
      }
      this.store.dispatch(recordPromptFromMicrophone());
   }

   clearRecordedData() {
      this.blobUrl = null;
      if (this.audioSrc) {
         URL.revokeObjectURL(this.audioSrc);
         this.audioSrc = null;
      }
      this.recording = '';
      this.audioRecordingService.clearRecordedBlob();
      this.store.dispatch(clearMicrophoneRecording());
      this.form.patchValue({
         file: null,
         fileAttachment: null,
      });
      this.fileData = null;
   }

   clearPromptFields() {
      this.form.get('destinationExtension')?.setValue(null);
      this.form.get('destinationNumber')?.setValue(null);
      this.form.get('fileAttachment')?.setValue(null);
      this.form.get('name')?.setValue(null);
   }

   ngOnDestroy(): void {
      this.abortRecording();
   }

   saveRecording() {
      const file = new File([this.fileData.blob], this.fileData.title);
      this.form.patchValue({
         file: {
            name: this.fileData.title,
            // eslint-disable-next-line sort-keys
            metaTag: this.fileData.title,
         },
         fileAttachment: file,
      });
   }

   validateRecording(): boolean {
      return this.fileData || this.recording === 'Stopped' ? false : true;
   }

   onCancelPromptRecordingCall() {
      this.recordingPromptCallCancelled.emit();
   }

   private formatDate(date = new Date()): string {
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
   }
}
